let counter = 1;

type RejectHandler = (error: unknown) => unknown;

export interface CrudQueueTimeoutDetails {
  queueName: string;
  step: number;
  executorName: string;
  timeoutMs: number;
  queuedAt: string;
  startedAt: string;
  timedOutAt: string;
  waitMs: number;
  elapsedMs: number;
  /** Steps still unfinished behind this one; the reported step itself is excluded. */
  stepsQueuedAfter: number;
  enqueueStack: string;
}

export interface CrudQueueFailureDetails extends Omit<CrudQueueTimeoutDetails, "timedOutAt"> {
  failedAt: string;
  afterTimeout: boolean;
}

interface CrudQueueOptions {
  /** Maximum execution wait per step, excluding time spent waiting in the queue. */
  timeoutMs?: number;
  /** Optional reporting hook; its completion never holds up the queue. */
  onTimeout?: (details: CrudQueueTimeoutDetails) => unknown;
  /** Reports executor failures, including late failures after timeout. Never awaited. */
  onError?: (error: unknown, details: CrudQueueFailureDetails) => unknown;
}

export class CrudQueueTimeoutError extends Error {
  name = "CrudQueueTimeoutError";

  constructor(readonly details: CrudQueueTimeoutDetails) {
    super(`${details.queueName} step #${details.step} timed out after ${details.timeoutMs}ms`);
  }
}

function isDebugEnabled() {
  return Boolean((globalThis as { DEBUG?: boolean }).DEBUG);
}

/**
 * Runs jobs in FIFO order with a configurable execution timeout (30 seconds by default).
 * This pragmatic safety limit leaves room for user gestures while preventing an
 * accidentally unresolved promise from blocking the queue indefinitely.
 * A timed-out step rejects and releases the queue, but its executor cannot be cancelled:
 * it may continue and overlap subsequent jobs. Timers cannot interrupt synchronous code
 * or guarantee an exact deadline while the JavaScript event loop is blocked.
 */
export default class CrudQueue {
  name = "";

  /** Total number of jobs ever enqueued; it never decreases. */
  queuedCount = 0;

  /** Total number of jobs that have finished, successfully or not. */
  finishedCount = 0;

  stacks: string[] = [];

  ongoing: Promise<void> = Promise.resolve();

  /** Optional failure observer, in addition to diagnostics; never awaited. */
  handleReject: RejectHandler = () => {};

  readonly timeoutMs: number;

  private readonly onTimeout?: CrudQueueOptions["onTimeout"];
  private readonly onError?: CrudQueueOptions["onError"];

  /** Jobs that are enqueued but not finished yet, the running one included. */
  get pendingCount() {
    return this.queuedCount - this.finishedCount;
  }

  constructor(name = `Queue #${counter++}`, { timeoutMs = 30000, onTimeout, onError }: CrudQueueOptions = {}) {
    if (!Number.isInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 2147483647) {
      throw new RangeError("timeoutMs must be an integer between 1 and 2147483647");
    }

    this.name = name;
    this.timeoutMs = timeoutMs;
    this.onTimeout = onTimeout;
    this.onError = onError;
  }

  debug(...message: unknown[]) {
    if (isDebugEnabled()) {
      try {
        console.log(this.name, ...message);
      } catch {
        // A logging failure must not interrupt queue execution.
      }
    }
  }

  private reportTimeout(error: CrudQueueTimeoutError) {
    try {
      console.warn(`${error.message}; continuing the queue without cancelling the executor`, error.details);
    } catch {
      // Reporting must not prevent timeout recovery.
    }

    try {
      void Promise.resolve(this.onTimeout?.(error.details)).catch(() => undefined);
    } catch {
      // Reporting hooks are best-effort, including synchronous failures.
    }
  }

  private reportFailure(error: unknown, details: CrudQueueFailureDetails) {
    try {
      console.error(`${details.queueName} step #${details.step} failed`, { ...details, error });
    } catch {
      // Reporting must not affect the job's result or queue recovery.
    }

    try {
      void Promise.resolve(this.onError?.(error, details)).catch(() => undefined);
    } catch {
      // Error reporters are best-effort, just like timeout reporters.
    }
  }

  queue<T>(fn: () => T | PromiseLike<T>, handleReject: RejectHandler = this.handleReject): Promise<T> {
    const currentStep = this.queuedCount++;
    const queuedAt = Date.now();
    // Capture the caller even when DEBUG is off so a production timeout is traceable.
    const enqueueStack = new Error().stack ?? "";

    if (isDebugEnabled()) {
      this.stacks.push(enqueueStack);
      this.debug(`Queued fn #${currentStep}`);
      this.debug('EnqueueStack', enqueueStack.replace('Error', ''));
    }

    const result = this.ongoing.then(async () => {
      const startedAt = Date.now();
      let timer: ReturnType<typeof setTimeout> | undefined;
      let timedOut = false;
      this.debug(`Running queued fn #${currentStep}`);

      const detailsAt = (timestamp: number) => ({
        queueName: this.name,
        step: currentStep,
        executorName: fn.name || "anonymous",
        timeoutMs: this.timeoutMs,
        queuedAt: new Date(queuedAt).toISOString(),
        startedAt: new Date(startedAt).toISOString(),
        waitMs: startedAt - queuedAt,
        elapsedMs: timestamp - startedAt,
        // While this step runs, count everything queued behind it; once it is over
        // (a late failure after a timeout), count what is genuinely still unfinished.
        stepsQueuedAfter: this.queuedCount - Math.max(this.finishedCount, currentStep + 1),
        enqueueStack
      });

      try {
        const timeout = new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => {
            const timedOutAt = Date.now();
            timedOut = true;
            const error = new CrudQueueTimeoutError({
              ...detailsAt(timedOutAt),
              timedOutAt: new Date(timedOutAt).toISOString()
            });
            reject(error);
            this.reportTimeout(error);
          }, this.timeoutMs);
        });

        const execution = Promise.resolve()
          .then(fn)
          .catch((error) => {
            const failedAt = Date.now();
            this.reportFailure(error, { ...detailsAt(failedAt), failedAt: new Date(failedAt).toISOString(), afterTimeout: timedOut });
            throw error;
          });

        // The race observes late executor rejections; the executor reports them too.
        return await Promise.race([execution, timeout]);
      } finally {
        clearTimeout(timer);
        this.finishedCount++;
        this.debug(`Finished queued fn #${currentStep}`);
      }
    });

    this.ongoing = result.then(
      () => undefined,
      (error) => {
        // Rejection reporting is deliberately best-effort. A faulty or pending
        // handler must not poison the internal tail and stop later jobs.
        try {
          void Promise.resolve(handleReject(error)).catch(() => undefined);
        } catch {
          // The queued job's promise still rejects with its original error.
        }
      }
    );

    return result;
  }
}
